import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { ArrowRight, Smartphone } from 'lucide-react';
import AppCard from './components/AppCard';
import { apps, developerPageUrl } from './data/apps';

const AppsPage = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Android Apps by Kabett",
    "description": "Collection of Android apps including Kannada Janapada, Hanuman Chalisa, Hindu Gods Quiz, and Pranayama",
    "numberOfItems": apps.length,
    "itemListElement": apps.map((app, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "MobileApplication",
        "name": app.name,
        "description": app.description,
        "applicationCategory": app.category,
        "operatingSystem": "Android",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        },
        "url": `https://kabett.com/apps/${app.slug}`,
        "installUrl": app.playStoreUrl
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Android Apps by Kabett | Kannada Janapada, Hanuman Chalisa & More</title>
        <meta name="description" content="Discover Android apps by Kabett - Kannada Janapada folk songs, Hindu Gods Quiz, Hanuman Chalisa in Hindi & Kannada, and Pranayama breathing exercises. Download free on Google Play." />
        <meta name="keywords" content="kabett apps, kannada janapada app, hanuman chalisa app, hindu gods quiz, pranayama app, kannada apps, indian apps" />
        <link rel="canonical" href="https://kabett.com/apps" />
        <meta property="og:title" content="Android Apps by Kabett" />
        <meta property="og:description" content="Discover our collection of Android apps - folk music, devotional apps, educational quizzes, and wellness tools." />
        <meta property="og:url" content="https://kabett.com/apps" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="mb-16">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-[#BAB37A] p-2 rounded-lg">
            <Smartphone className="text-white" size={24} />
          </div>
          <span className="text-[#BAB37A] font-semibold">Our Apps</span>
        </div>
        <h1 className="text-4xl md:text-5xl font-serif font-bold mb-6">
          Android Apps by Kabett
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mb-8">
          From Kannada folk music to devotional apps and wellness tools, we create apps that celebrate Indian culture and help you live better. All our apps are free to download on Google Play.
        </p>
        <a
          href={developerPageUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-[#BAB37A] hover:text-[#9A9463] font-semibold"
        >
          <span>View all on Google Play</span>
          <ArrowRight size={20} />
        </a>
      </section>

      {/* Apps Grid */}
      <section className="mb-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {apps.map((app, index) => (
            <motion.div
              key={app.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <AppCard app={app} />
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#F3F1E4] rounded-lg p-8 text-center">
        <h2 className="text-2xl font-serif font-bold mb-4">
          More Apps Coming Soon
        </h2>
        <p className="text-gray-600 mb-6">
          We're constantly working on new apps to help you stay connected to Indian culture and improve your daily life.
        </p>
        <a
          href={developerPageUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#BAB37A] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#9A9463] transition-colors"
        >
          <span>Follow us on Google Play</span>
          <ArrowRight size={20} />
        </a>
      </section>
    </>
  );
};

export default AppsPage;
